import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { filterStringPropertiesByValue } from '@common/utils/array-utils';
import { Confirmer, CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import {
  AUTHORIZATION_ACTIONS,
  AuthorizationActions
} from '@/app/platform/platform-data/actions/types';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { USER_GROUP_QUERIES, UserGroupQueries } from '@/app/platform/platform-data/queries/types';
import { EgiAppSelectComponent } from '@/app/platform/platform-data/components/form-controls/egi-app-select/egi-app-select.component';

@AutoUnsubscribe()
@Component({
    selector: 'user-group-app-links[activeUserGroup]',
    templateUrl: './user-group-app-links.component.html',
    styleUrls: ['./user-group-app-links.component.css'],
    standalone: false
})
export class UserGroupAppLinksComponent implements OnInit, OnDestroy {
  @Input('activeUserGroup') set _(v: UserGroupEntity) {
    if (this.activeUserGroup?.isNot(v)) {
      this.resetActiveApp();
    }
    this.activeUserGroup = v;
    this.subscribeToLinkedAppsForGroupId();
  }

  @Input('filteredApp') set filteredAppInput(v: EgiAppEntity | undefined) {
    this.filteredApp = v;

    if (v) {
      this.selectActiveApp(v);
    }
  }

  filteredApp?: EgiAppEntity;

  @ViewChild('appSelect', { static: true }) appSelectRef: EgiAppSelectComponent;

  constructor(
    @Inject(USER_GROUP_QUERIES)
    private readonly userGroupQueries: UserGroupQueries,
    @Inject(AUTHORIZATION_ACTIONS)
    private readonly authorizationActions: AuthorizationActions,
    @Inject(CONFIRMER)
    private readonly confirmer: Confirmer
  ) {}

  activeUserGroup = UserGroupEntity.createDefault();
  activeApp: EgiAppEntity = EgiAppEntity.createDefault();

  linkedAppsSubscription: Subscription;

  linkedApps: EgiAppEntity[] = [];
  appToLink: EgiAppEntity = EgiAppEntity.createDefault();
  searchText = '';

  get filteredLinkedApps(): EgiAppEntity[] {
    return filterStringPropertiesByValue(this.linkedApps, this.searchText);
  }

  ngOnInit(): void {}

  selectActiveApp(a: EgiAppEntity) {
    this.activeApp = a;
  }

  resetActiveApp() {
    this.activeApp = EgiAppEntity.createDefault();
  }

  async linkNewAppToUserGroup() {
    const result = await this.authorizationActions.linkAppToGroup(
      this.activeUserGroup,
      this.appToLink
    );
    result.ifSuccess(() => {
      this.activeApp = this.appToLink;
      this.appToLink = undefined;
      this.appSelectRef.clear();
    });
  }

  async onRemove(app: EgiAppEntity, event: Event) {
    event.stopPropagation();
    const confirmation = await this.confirmer.confirm(
      'Bent u zeker dat u deze app wilt verwijderen voor de groep?'
    );
    if (confirmation) {
      const result = await this.authorizationActions.unlinkAppFromGroup(this.activeUserGroup, app);
      result.ifSuccess(() => (this.activeApp = EgiAppEntity.createDefault()));
    }
  }

  ngOnDestroy(): void {
    this.linkedAppsSubscription.unsubscribe();
  }

  private subscribeToLinkedAppsForGroupId(): void {
    if (this.linkedAppsSubscription) {
      this.linkedAppsSubscription.unsubscribe();
    }

    if (this.activeUserGroup && this.activeUserGroup.isNotDefault) {
      this.linkedAppsSubscription = this.userGroupQueries
        .egiAppsForGroup$(this.activeUserGroup.id)
        .subscribe(apps => (this.linkedApps = apps));
    }
  }
}
